import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { GlobalSearchService } from './globalsearchservice.service';
import { RuntimeConfigLoaderService } from 'runtime-config-loader';

const METRC_LICENSE_STORAGE_KEY = 'metrc.activeLicense';

export interface MetrcStandardResponse<T = any> {
	success?: boolean;
	error?: string;
	data?: T;
	[key: string]: any;
}

export interface MetrcHealthSummary {
	success: boolean;
	enabled: boolean;
	api_reachable: boolean;
	default_license: string;
	queue: { pending: number; running: number; failed: number; done: number };
	last_sync: Array<{
		license_number: string;
		resource_key: string;
		last_modified_cursor: string | null;
		last_success_at: string | null;
		last_error: string | null;
	}>;
	failures: Array<{
		id: number;
		job_type: string;
		license_number: string;
		attempts: number;
		last_error: string;
		updated_at: string;
	}>;
}

export interface MetrcPackage {
	id?: number;
	license_number: string;
	package_label: string;
	package_id: number;
	item_name: string | null;
	item_id: number;
	quantity: number | null;
	unit_of_measure: string | null;
	location_name: string | null;
	package_type: string | null;
	source: 'active' | 'onhold' | 'intransit' | 'inactive';
	last_seen_at: string;
	payload_json?: string | null;
	/** From list endpoint when DB columns exist */
	has_detail?: number | boolean;
	has_lab?: number | boolean;
	enriched_at?: string | null;
}

export interface MetrcPackageDetailSnapshotFields {
	id: number;
	license_number: string;
	package_label: string;
	package_id: number;
	item_name: string;
	item_id: number;
	quantity: number | string | null;
	unit_of_measure: string;
	location_name: string;
	package_type: string;
	source: string;
	last_seen_at: string;
	enriched_at?: string | null;
}

export interface MetrcPackageMappedRow {
	stkcode: string;
	metrc_item_id?: number | null;
	match_item_name?: string | null;
	license_number?: string;
}

export interface MetrcPackageDetailPayload {
	success?: boolean;
	snapshot_fields: MetrcPackageDetailSnapshotFields;
	item: Record<string, unknown> | null;
	package_merged: Record<string, unknown> | null;
	list_payload: Record<string, unknown> | null;
	detail_envelope: unknown;
	detail_body: unknown;
	lab_results: unknown;
	mapped: MetrcPackageMappedRow | null;
	live_metrc: unknown;
}

export interface MetrcTransferCacheRow {
	id: number;
	license_number: string;
	direction: 'incoming' | 'outgoing' | 'rejected';
	transfer_id: number;
	manifest_number: string | null;
	payload_json: string | null;
	synced_at: string;
	/** ERP sales order when this Metrc transfer was created from order sync */
	linked_orderno?: number | null;
}

export interface MetrcMappingCandidate {
	item_id: number;
	name: string;
	license_number: string;
	score: number;
	match_type: 'exact' | 'token';
}

export interface MetrcMappingSuggestionRow {
	stockid: string;
	description: string;
	candidates: MetrcMappingCandidate[];
	match_count: number;
	mapping_id?: number;
	mapping_mode?: string;
}

export interface MetrcSuggestScanResponse {
	success: boolean;
	license_number: string;
	scanned: number;
	auto_mapped: number;
	needs_review: number;
	unmatched: number;
	auto_mapped_rows: MetrcMappingSuggestionRow[];
	review_rows: MetrcMappingSuggestionRow[];
	unmatched_rows: MetrcMappingSuggestionRow[];
	errors: string[];
}

export interface MetrcInventoryStockmasterCandidate {
	stkcode: string;
	description: string;
	hits: number;
	token_total: number;
	score: number;
	match_type: 'strong' | 'medium' | 'weak';
}

export interface MetrcInventorySuggestionRow {
	item_id: number;
	item_name: string;
	qty_active: number;
	qty_onhold: number;
	qty_intransit: number;
	pkg_count: number;
	candidates: MetrcInventoryStockmasterCandidate[];
	match_count: number;
	mapping_id?: number;
	mapping_mode?: string;
}

export interface MetrcInventoryScanResponse {
	success: boolean;
	license_number: string;
	scanned: number;
	auto_mapped: number;
	needs_review: number;
	unmatched: number;
	auto_mapped_rows: MetrcInventorySuggestionRow[];
	review_rows: MetrcInventorySuggestionRow[];
	unmatched_rows: MetrcInventorySuggestionRow[];
	errors: string[];
}

export interface MetrcStockMappingRow {
	id?: number;
	stkcode: string;
	license_number: string;
	metrc_item_id: number | null;
	match_item_name: string | null;
	active: number;
	notes?: string | null;
	created_at?: string;
	updated_at?: string;
}

/** Response from POST metrc/mappings/claudeSuggestMapping (Anthropic tool pick, same key resolution as PO PDF). */
export interface MetrcClaudeMappingPick {
	chosen_index: number;
	confidence: number | null;
	reason: string;
	chosen_candidate: Record<string, unknown> | null;
}

export interface MetrcClaudeMappingSuggestResponse {
	success: boolean;
	message?: string | null;
	mapping_pick?: MetrcClaudeMappingPick | null;
	claude?: {
		ok?: boolean;
		pick?: MetrcClaudeMappingPick;
		message?: string;
		diagnostic?: unknown;
		model?: string;
	};
}

export interface MetrcOrderSyncRow {
	orderno: number;
	debtorno?: string;
	customer_name?: string;
	orddate?: string;
	license_number: string;
	state: string;
	metrc_transfer_id: string | null;
	metrc_delivery_id: string | null;
	metrc_manifest_id: string | null;
	manifest_pdf_path?: string | null;
	last_error: string | null;
	updated_at: string;
}

export interface MetrcCustomerManifestRow {
	orderno: string;
	orddate: string;
	debtorno: string;
	customer_name: string;
	recipient_license: string;
	metrc_sync_state: string;
	metrc_transfer_id: string;
	metrc_manifest_id: string;
	manifest_pdf_path: string;
	last_error: string;
	metrc_updated_at: string;
	has_manifest_pdf: boolean;
}

export interface MetrcCustomerManifestsResponse extends MetrcStandardResponse {
	debtorno?: string;
	rows?: MetrcCustomerManifestRow[];
	total?: number;
	manifest_pdf_route?: string;
	manifest_pdf_note?: string;
}

export interface MetrcRecipientLicenseStatus extends MetrcStandardResponse {
	debtorno?: string;
	name?: string;
	cosm_license?: string;
	licExpDate?: string;
	checks?: { has_recipient_license: boolean; license_expiry_ok: boolean | null; pattern_ok: boolean | null };
	seen_as_recipient_in_transfer_cache?: number;
	seen_as_recipient_in_metrc_outgoing_first_page?: boolean | null;
	metrc_outgoing_scan_error?: string | null;
	ready_for_wholesale_transfer?: boolean;
	warnings?: string[];
}

export interface MetrcReceiptSyncRow {
	id: number;
	sale_id: string;
	license_number: string;
	state: string;
	metrc_receipt_id: string | null;
	external_number: string | null;
	finalized: number;
	last_error: string | null;
	updated_at: string;
}

export interface MetrcQueueJob {
	id: number;
	job_type: string;
	license_number: string;
	status: 'pending' | 'running' | 'done' | 'failed';
	attempts: number;
	last_error: string | null;
	run_after: string;
	created_at: string;
	updated_at: string;
	payload_json?: string | null;
}

export interface MetrcRuntimeConfig {
	metrc_enabled: number;
	metrc_api_url: string;
	metrc_vendor_key: string;
	metrc_user_key_set: boolean;
	metrc_default_license: string;
	metrc_webhook_secret_set: boolean;
	updated_by: string | null;
	updated_at: string | null;
}

export interface MetrcRuntimeConfigUpdate {
	metrc_enabled?: number | boolean;
	metrc_api_url?: string;
	metrc_vendor_key?: string;
	metrc_user_key?: string;
	metrc_default_license?: string;
	metrc_webhook_secret?: string;
}

export interface MetrcOrderImportRow {
	id: number;
	source: 'receipt' | 'transfer';
	direction: string | null;
	license_number: string;
	natural_key: string;
	metrc_receipt_id: number | null;
	metrc_transfer_id: number | null;
	metrc_delivery_id: number | null;
	metrc_manifest_number: string | null;
	business_date: string | null;
	debtorno: string | null;
	orderno: number | null;
	transno: number | null;
	total_subtotal: string;
	total_tax: string;
	total_amount: string;
	inventory_mode: string;
	posting_mode: string;
	state: string;
	dry_run: number;
	import_run_id: string | null;
	error_json: string | null;
	posted_at: string | null;
	created_at: string;
	updated_at: string;
}

export interface MetrcOrderImportLine {
	id: number;
	line_index: number;
	package_label: string;
	package_id: number | null;
	metrc_item_id: number | null;
	metrc_product_name: string;
	stkcode: string | null;
	quantity: string;
	unit_of_measure: string;
	unit_price: string | null;
	line_subtotal: string;
	line_discount: string;
	line_tax: string;
	line_total: string;
	status: string;
	reason: string | null;
	message: string | null;
	orderlineno: number | null;
	stkmoveno: number | null;
}

export interface MetrcOrderImportStatRow {
	source: string;
	state: string;
	c: string;
	amt: string | null;
}

export interface MetrcOrderImportCacheRow {
	k: string;
	c: string;
}

export interface MetrcOrderImportStats {
	success: boolean;
	license: string;
	by_source_state: MetrcOrderImportStatRow[];
	cache: MetrcOrderImportCacheRow[];
}

export interface MetrcOrderImportOpts {
	license: string;
	max?: number;
	source?: string;
	direction?: string;
	start?: string;
	end?: string;
	only_missing?: 0 | 1;
	dry_run?: 0 | 1;
	posting_mode?: string;
	inventory_mode?: string;
	debtorno?: string;
}

export interface MetrcOrderImportResult {
	ok?: boolean;
	success?: boolean;
	error?: string;
	imported?: number;
	posted?: number;
	failed?: number;
	skipped?: number;
	unmapped?: number;
	dry_run?: boolean | number;
	job_id?: number;
	job_type?: string;
	payload?: any;
}

export interface MetrcUnmappedLine {
	import_id: number;
	line_index: number;
	package_label: string;
	metrc_item_id: number | null;
	metrc_product_name: string;
	stkcode: string | null;
	quantity: string;
	unit_of_measure: string;
	line_total: string;
	status: string;
	reason: string | null;
	message: string | null;
	source: string;
	license_number: string;
	natural_key: string;
	business_date: string | null;
}

export interface MetrcReconcileReport {
	ok?: boolean;
	success?: boolean;
	error?: string;
	license?: string;
	start?: string;
	end?: string;
	rows?: Array<{
		business_date: string;
		source: string;
		metrc_count: number;
		metrc_total: string;
		erp_count: number;
		erp_total: string;
		delta_count: number;
		delta_amount: string;
		mismatch: boolean;
	}>;
	summary?: {
		metrc_total: string;
		erp_total: string;
		delta: string;
		mismatch_days: number;
	};
}

@Injectable({ providedIn: 'root' })
export class MetrcService {
	private config: any;
	private user: any = [];
	private configChangedSource = new Subject<void>();
	configChanged$ = this.configChangedSource.asObservable();
	private activeLicenseSource = new BehaviorSubject<string>(this.readStoredLicense());
	activeLicense$ = this.activeLicenseSource.asObservable().pipe(distinctUntilChanged());

	constructor(
		private http: HttpClient,
		private globalSearchService: GlobalSearchService,
		private configSvc: RuntimeConfigLoaderService
	) {
		this.globalSearchService.configsubscription.subscribe(r => {
			this.config = r;
		});
		this.globalSearchService.user.subscribe(result => {
			this.user = result;
		});
	}

	notifyConfigChanged(): void {
		this.configChangedSource.next();
	}

	/** Returns the raw API server base URL (no trailing slash). Used to build direct asset URLs. */
	getApiBase(): string {
		return this.config?.apiServer?.endpoint
			? String(this.config.apiServer.endpoint).replace(/\/$/, '')
			: '';
	}

	getActiveLicense(): string {
		return (this.activeLicenseSource.value || '').trim();
	}

	setActiveLicense(license: string | null | undefined): void {
		const next = (license || '').trim();
		if (next === this.activeLicenseSource.value) {
			return;
		}
		this.activeLicenseSource.next(next);
		try {
			if (typeof window !== 'undefined' && window.sessionStorage) {
				if (next) {
					window.sessionStorage.setItem(METRC_LICENSE_STORAGE_KEY, next);
				} else {
					window.sessionStorage.removeItem(METRC_LICENSE_STORAGE_KEY);
				}
			}
		} catch (_) { /* sessionStorage may be unavailable in some contexts */ }
	}

	private readStoredLicense(): string {
		try {
			if (typeof window !== 'undefined' && window.sessionStorage) {
				return (window.sessionStorage.getItem(METRC_LICENSE_STORAGE_KEY) || '').trim();
			}
		} catch (_) { /* ignore */ }
		return '';
	}

	private endpoint(path: string, query: { [k: string]: any } = {}): string {
		const base = this.config.apiServer.endpoint;
		const sep = base.indexOf('?') === -1 ? '?' : '&';
		const parts: string[] = this.authPairs();
		Object.keys(query).forEach(k => {
			const v = query[k];
			if (v === null || v === undefined || v === '') return;
			parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(String(v)));
		});
		const tail = parts.length ? sep + parts.join('&') : '';
		return base + path + tail;
	}

	private authPairs(): string[] {
		const pkg = this.globalSearchService.getSessionItem('package');
		const userid = this.user && this.user.user ? this.user.user.userid : '';
		const pairs = [
			'key=' + encodeURIComponent(this.config.apiCredentials.clientId),
			'userid=' + encodeURIComponent(String(userid))
		];
		if (pkg) {
			pairs.push('package=' + encodeURIComponent(String(pkg)));
		}
		return pairs;
	}

	health(): Observable<MetrcHealthSummary> {
		return this.http.get<MetrcHealthSummary>(this.endpoint('metrc/health/summary'));
	}

	pingApi(): Observable<MetrcStandardResponse> {
		return this.http.get<MetrcStandardResponse>(this.endpoint('metrc/health/ping'));
	}

	listPackages(source: 'active' | 'onhold' | 'intransit', filter: { license?: string; q?: string; limit?: number; offset?: number; sort?: 'label' | 'recent' } = {}):
		Observable<{ success: boolean; rows: MetrcPackage[]; total: number; source?: string; license?: string; sort?: string; offset?: number; limit?: number }> {
		return this.http.get<{ success: boolean; rows: MetrcPackage[]; total: number; source?: string; license?: string; sort?: string; offset?: number; limit?: number }>(
			this.endpoint('metrc/packages/list', { source, ...filter })
		);
	}

	getPackageDetail(params: { id: number; license?: string; source?: string; live?: boolean }): Observable<MetrcPackageDetailPayload> {
		const q: { [k: string]: string | number } = { id: params.id };
		if (params.license) {
			q.license = params.license;
		}
		if (params.source) {
			q.source = params.source;
		}
		if (params.live) {
			q.live = 1;
		}
		return this.http.get<MetrcPackageDetailPayload>(this.endpoint('metrc/packages/detail', q));
	}

	searchPackagesByLabel(label: string, license?: string): Observable<{ success: boolean; rows: MetrcPackage[] }> {
		return this.http.get<{ success: boolean; rows: MetrcPackage[] }>(
			this.endpoint('metrc/packages/search', { label, license })
		);
	}

	getPackageLive(label: string, license?: string): Observable<MetrcStandardResponse> {
		return this.http.get<MetrcStandardResponse>(this.endpoint('metrc/packages/live', { label, license }));
	}

	pullPackages(source: 'active' | 'onhold' | 'intransit', license?: string):
		Observable<MetrcStandardResponse & { job_id?: number; job_type?: string; source?: string; license?: string }> {
		return this.http.post<MetrcStandardResponse & { job_id?: number; job_type?: string; source?: string; license?: string }>(
			this.endpoint('metrc/packages/pull'), { source, license }
		);
	}

	/**
	 * Split a Metrc package into 1..n new packages. Server queues a `package_create` job and the
	 * worker hits POST /packages/v2/ with Ingredients=[{ Package: source_package_label, ... }].
	 * Both the new tags and the source label get refreshed in the local snapshot afterwards.
	 */
	splitPackage(body: {
		source_package_label: string;
		splits: Array<{
			tag: string;
			quantity: number;
			unit_of_measure?: string;
			item_name?: string;
			note?: string;
			packaged_date?: string;
			production_batch_number?: string;
			is_production_batch?: boolean;
			is_donation?: boolean;
		}>;
		license?: string;
		source_item_name?: string;
		source_unit_of_measure?: string;
		packaged_date?: string;
	}): Observable<MetrcStandardResponse & { job_id?: number; deduped?: boolean }> {
		return this.http.post<MetrcStandardResponse & { job_id?: number; deduped?: boolean }>(
			this.endpoint('metrc/package_commands/split'),
			body
		);
	}

	/**
	 * Poll status of an enqueued package_* job (adjust / location / finish / unfinish / create / split).
	 * Resolves via `apir/metrc/package_commands/status`.
	 */
	getPackageJobStatus(opts: { job_id?: number; client_ref?: string; license?: string }): Observable<MetrcStandardResponse & { job?: MetrcQueueJob }> {
		return this.http.post<MetrcStandardResponse & { job?: MetrcQueueJob }>(
			this.endpoint('metrc/package_commands/status'),
			opts
		);
	}

	listTransfers(direction: 'outgoing' | 'incoming', filter: { license?: string; manifest?: string; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: MetrcTransferCacheRow[]; total: number }> {
		return this.http.get<{ success: boolean; rows: MetrcTransferCacheRow[]; total: number }>(
			this.endpoint('metrc/transfers/list', { direction, ...filter })
		);
	}

	getTransfer(transferId: number, license?: string): Observable<MetrcStandardResponse> {
		return this.http.get<MetrcStandardResponse>(this.endpoint('metrc/transfers/get', { transfer_id: transferId, license }));
	}

	createOutgoingTransfer(orderno: number): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/transfers/create'), { orderno });
	}

	getManifestPdfUrl(orderno: number): string {
		return this.endpoint('metrc/transfers/manifestPdf', { orderno });
	}

	fetchManifestPdf(orderno: number): Observable<Blob> {
		return this.http.get(this.endpoint('metrc/transfers/manifestPdf', { orderno }), { responseType: 'blob' });
	}

	/**
	 * Queue manifest PDF download. Pass ERP orderno, or Metrc transfer_id (resolved via metrc_order_sync).
	 */
	enqueueManifestPdf(opts: number | { orderno?: number; transfer_id?: number }): Observable<MetrcStandardResponse> {
		const body: { orderno?: number; transfer_id?: number } = {};
		if (typeof opts === 'number') {
			if (opts > 0) {
				body.orderno = opts;
			}
		} else {
			if (opts.orderno != null && opts.orderno > 0) {
				body.orderno = opts.orderno;
			}
			if (opts.transfer_id != null && opts.transfer_id > 0) {
				body.transfer_id = opts.transfer_id;
			}
		}
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/transfers/enqueueManifest'), body);
	}

	listMappings(filter: { stkcode?: string; license?: string; mapped?: 0 | 1; active?: 0 | 1; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: MetrcStockMappingRow[]; total: number }> {
		return this.http.get<{ success: boolean; rows: MetrcStockMappingRow[]; total: number }>(
			this.endpoint('metrc/mappings/list', filter)
		);
	}

	getMapping(id: number): Observable<{ success: boolean; row: MetrcStockMappingRow }> {
		return this.http.get<{ success: boolean; row: MetrcStockMappingRow }>(this.endpoint('metrc/mappings/get', { id }));
	}

	saveMapping(row: MetrcStockMappingRow): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/mappings/save'), row);
	}

	deleteMapping(id: number): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/mappings/delete'), { id });
	}

	bulkImportMappings(rows: MetrcStockMappingRow[]): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/mappings/bulkImport'), { rows });
	}

	/**
	 * One row at a time: source + candidates → Claude JSON tool pick. Server proxies Anthropic;
	 * failures still return a body with `claude` diagnostics (HTTP 200).
	 */
	claudeSuggestMapping(body: {
		mode: 'catalog' | 'inventory';
		license?: string;
		license_number?: string;
		source: Record<string, unknown>;
		candidates: Array<Record<string, unknown>>;
	}): Observable<MetrcClaudeMappingSuggestResponse> {
		return this.http.post<MetrcClaudeMappingSuggestResponse>(
			this.endpoint('metrc/mappings/claudeSuggestMapping'),
			body
		);
	}

	suggestUnmapped(limit: number = 100): Observable<MetrcSuggestScanResponse> {
		return this.http.get<MetrcSuggestScanResponse>(this.endpoint('metrc/mappings/suggestUnmapped', { limit }));
	}

	suggestUnmappedScan(opts: { license?: string; limit?: number; include_unmatched?: 0 | 1; auto_apply?: 0 | 1 } = {}):
		Observable<MetrcSuggestScanResponse> {
		return this.http.get<MetrcSuggestScanResponse>(this.endpoint('metrc/mappings/suggest', opts));
	}

	autoMapSingleMatches(opts: { license?: string; limit?: number; include_unmatched?: 0 | 1 } = {}):
		Observable<MetrcSuggestScanResponse> {
		return this.http.post<MetrcSuggestScanResponse>(this.endpoint('metrc/mappings/autoMap'), opts);
	}

	inventorySuggestScan(opts: { license?: string; limit?: number; only_in_stock?: 0 | 1; include_unmatched?: 0 | 1; auto_apply?: 0 | 1; min_token_len?: number } = {}):
		Observable<MetrcInventoryScanResponse> {
		return this.http.get<MetrcInventoryScanResponse>(this.endpoint('metrc/mappings/inventorySuggest', opts));
	}

	autoMapInventorySingles(opts: { license?: string; limit?: number; only_in_stock?: 0 | 1; include_unmatched?: 0 | 1; min_token_len?: number } = {}):
		Observable<MetrcInventoryScanResponse> {
		return this.http.post<MetrcInventoryScanResponse>(this.endpoint('metrc/mappings/inventoryAutoMap'), opts);
	}

	searchMetrcItems(q: string, license?: string, limit: number = 100, offset: number = 0):
		Observable<{ success: boolean; rows: any[]; total: number }> {
		return this.http.get<{ success: boolean; rows: any[]; total: number }>(
			this.endpoint('metrc/mappings/searchItems', { q, license, limit, offset })
		);
	}

	searchStockmaster(q: string, limit: number = 100, offset: number = 0):
		Observable<{ success: boolean; rows: Array<{ stkcode: string; description: string }>; total: number }> {
		return this.http.get<{ success: boolean; rows: Array<{ stkcode: string; description: string }>; total: number }>(
			this.endpoint('metrc/mappings/searchStockmaster', { q, limit, offset })
		);
	}

	listOrders(filter: { state?: string; license?: string; orderno?: string; debtorno?: string; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: MetrcOrderSyncRow[]; total: number }> {
		return this.http.get<{ success: boolean; rows: MetrcOrderSyncRow[]; total: number }>(
			this.endpoint('metrc/orders/list', filter)
		);
	}

	listCustomerManifests(debtorno: string, filter: { limit?: number; offset?: number } = {}): Observable<MetrcCustomerManifestsResponse> {
		return this.http.get<MetrcCustomerManifestsResponse>(this.endpoint('metrc/customers/manifests', { debtorno, ...filter }));
	}

	verifyCustomerRecipientLicense(debtorno: string, opts: { checkMetrcOutgoing?: boolean } = {}): Observable<MetrcRecipientLicenseStatus> {
		const q: { debtorno: string; check_metrc_outgoing?: number } = { debtorno };
		if (opts.checkMetrcOutgoing) {
			q.check_metrc_outgoing = 1;
		}
		return this.http.get<MetrcRecipientLicenseStatus>(this.endpoint('metrc/customers/verifyLicense', q));
	}

	getOrder(orderno: number): Observable<MetrcStandardResponse> {
		return this.http.get<MetrcStandardResponse>(this.endpoint('metrc/orders/get', { orderno }));
	}

	resyncOrder(orderno: number): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/orders/resync'), { orderno });
	}

	cancelOrderSync(orderno: number): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/orders/cancel'), { orderno });
	}

	listReceipts(filter: { state?: string; license?: string; sale_id?: string; finalized?: 0 | 1; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: MetrcReceiptSyncRow[]; total: number }> {
		return this.http.get<{ success: boolean; rows: MetrcReceiptSyncRow[]; total: number }>(
			this.endpoint('metrc/receipts/list', filter)
		);
	}

	pushReceipt(saleId: string): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/receipts/push'), { sale_id: saleId });
	}

	finalizeReceipt(saleId: string): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/receipts/finalize'), { sale_id: saleId });
	}

	unfinalizeReceipt(saleId: string): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/receipts/unfinalize'), { sale_id: saleId });
	}

	enqueueReceipt(saleId: string, type: 'push' | 'finalize' | 'unfinalize' = 'push'): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/receipts/enqueue'), { sale_id: saleId, type });
	}

	listQueue(filter: { status?: string; job_type?: string; license?: string; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: MetrcQueueJob[]; total: number }> {
		return this.http.get<{ success: boolean; rows: MetrcQueueJob[]; total: number }>(
			this.endpoint('metrc/queue/list', filter)
		);
	}

	queueStats(): Observable<{ success: boolean; counts: { pending: number; running: number; failed: number; done: number } }> {
		return this.http.get<{ success: boolean; counts: { pending: number; running: number; failed: number; done: number } }>(
			this.endpoint('metrc/queue/stats')
		);
	}

	retryJob(id: number): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/queue/retry'), { id });
	}

	killJob(id: number): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/queue/kill'), { id });
	}

	enqueueJob(jobType: string, payload: any = {}, license?: string):
		Observable<MetrcStandardResponse & { job_id?: number }> {
		const body: any = { job_type: jobType, payload };
		if (license !== undefined && license !== null && String(license).trim() !== '') {
			body.license = String(license).trim();
		}
		return this.http.post<MetrcStandardResponse & { job_id?: number }>(this.endpoint('metrc/queue/enqueue'), body);
	}

	purgeDoneJobs(olderThanDays: number = 30): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/queue/purgeDone'), { older_than_days: olderThanDays });
	}

	listItems(filter: { license?: string; q?: string; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: any[]; total: number }> {
		return this.http.get<{ success: boolean; rows: any[]; total: number }>(this.endpoint('metrc/reference/items', filter));
	}

	/** Synchronous pull from Metrc → DB cache, then returns first page of cached rows. */
	pullItemsFromMetrc(body: { license?: string; q?: string; limit?: number } = {}): Observable<{
		success: boolean;
		pulled?: boolean;
		items_synced?: number;
		license?: string;
		rows?: any[];
		total?: number;
		error?: string;
		message?: string;
	}> {
		return this.http.post<{
			success: boolean;
			pulled?: boolean;
			items_synced?: number;
			license?: string;
			rows?: any[];
			total?: number;
			error?: string;
			message?: string;
		}>(this.endpoint('metrc/reference/pullItems'), body);
	}

	listLocations(filter: { license?: string; q?: string; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: any[]; total: number }> {
		return this.http.get<{ success: boolean; rows: any[]; total: number }>(this.endpoint('metrc/reference/locations', filter));
	}

	liveReference(kind: 'unitsofmeasure' | 'transfertypes' | 'adjustreasons' | 'labstates' | 'customertypes' | 'paymenttypes', license?: string):
		Observable<MetrcStandardResponse> {
		return this.http.get<MetrcStandardResponse>(this.endpoint('metrc/reference/live', { kind, license }));
	}

	getRuntimeConfig(): Observable<{ success: boolean; config: MetrcRuntimeConfig }> {
		return this.http.get<{ success: boolean; config: MetrcRuntimeConfig }>(this.endpoint('metrc/config/get'));
	}

	saveRuntimeConfig(update: MetrcRuntimeConfigUpdate): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/config/save'), update);
	}

	testRuntimeConfig(update?: MetrcRuntimeConfigUpdate): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/config/test'), update || {});
	}

	// ── Order Import / Backfill ─────────────────────────────────────────────

	getOrderImportStats(license?: string): Observable<MetrcOrderImportStats> {
		return this.http.get<MetrcOrderImportStats>(this.endpoint('metrc/orderimport/stats', license ? { license } : {}));
	}

	listOrderImport(filter: { license?: string; source?: string; state?: string; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: MetrcOrderImportRow[]; total: number }> {
		return this.http.get<{ success: boolean; rows: MetrcOrderImportRow[]; total: number }>(
			this.endpoint('metrc/orderimport/list', filter)
		);
	}

	getOrderImportLines(importId: number): Observable<{ success: boolean; import_id: number; rows: MetrcOrderImportLine[] }> {
		return this.http.get<{ success: boolean; import_id: number; rows: MetrcOrderImportLine[] }>(
			this.endpoint('metrc/orderimport/lines', { import_id: importId })
		);
	}

	listOrderImportUnmapped(license?: string, limit: number = 200):
		Observable<{ success: boolean; rows: MetrcUnmappedLine[]; license: string }> {
		return this.http.get<{ success: boolean; rows: MetrcUnmappedLine[]; license: string }>(
			this.endpoint('metrc/orderimport/unmapped', { license: license || '', limit })
		);
	}

	getOrderImportReconcile(opts: { license: string; start?: string; end?: string; source?: string; tolerance?: number }):
		Observable<MetrcReconcileReport> {
		return this.http.get<MetrcReconcileReport>(this.endpoint('metrc/orderimport/reconcile', opts));
	}

	enqueueOrderImportReceipts(opts: MetrcOrderImportOpts): Observable<MetrcOrderImportResult> {
		return this.http.post<MetrcOrderImportResult>(this.endpoint('metrc/orderimport/enqueueReceipts'), opts);
	}

	enqueueOrderImportTransfers(opts: MetrcOrderImportOpts): Observable<MetrcOrderImportResult> {
		return this.http.post<MetrcOrderImportResult>(this.endpoint('metrc/orderimport/enqueueTransfers'), opts);
	}

	runOrderImportReceipts(opts: MetrcOrderImportOpts): Observable<MetrcOrderImportResult> {
		return this.http.post<MetrcOrderImportResult>(this.endpoint('metrc/orderimport/runReceipts'), opts);
	}

	runOrderImportTransfers(opts: MetrcOrderImportOpts): Observable<MetrcOrderImportResult> {
		return this.http.post<MetrcOrderImportResult>(this.endpoint('metrc/orderimport/runTransfers'), opts);
	}

	harvestUnmappedItems(license: string, noAutomap: boolean = false): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(
			this.endpoint('metrc/orderimport/harvest'),
			{ license, no_automap: noAutomap ? 1 : 0 }
		);
	}

	// ── Picking ────────────────────────────────────────────────────────────
	// RFID-tag-based picking workflow for orders held in awaiting_picking.
	// State is keyed by orderno; the server resolves the license off
	// metrc_order_sync.

	getPickingState(orderno: number, includeOptions = false): Observable<MetrcPickingState> {
		const q: any = { orderno };
		// Picker page asks for include_options=1 only on initial load; subsequent
		// scan/refresh hits skip the dropdown lists (saves 2 queries per round trip).
		if (includeOptions) q.include_options = 1;
		return this.http.get<MetrcPickingState>(this.endpoint('metrc/picking', q));
	}

	getPickingTransporterOptions(): Observable<{ success: boolean; drivers: MetrcAvailableDriver[]; vehicles: MetrcAvailableVehicle[] }> {
		return this.http.get<any>(this.endpoint('metrc/picking/transporters/options'));
	}

	getPickingQueue(): Observable<any> {
		// Goes through endpoint() so key + userid auth pairs land on the URL —
		// other apir/metrc/* endpoints depend on them; without them the API returns 401.
		return this.http.get<any>(this.endpoint('metrc/picking/queue'));
	}

	// No-scan completion for a pure non-Metrc (all-hemp) order — there is nothing to
	// RFID-scan, so this ships it directly and drops it off the picking queue.
	shipNonMetrc(orderno: number): Observable<any> {
		return this.http.post<any>(this.endpoint('metrc/picking/shipNonMetrc'), { orderno });
	}

	getReceivingQueue(): Observable<any> {
		return this.http.get<any>(this.endpoint('metrc/receiving/queue'));
	}

	getReceivingState(deliveryCacheId: number): Observable<MetrcReceivingState> {
		return this.http.get<MetrcReceivingState>(
			this.endpoint('metrc/receiving', { delivery_cache_id: deliveryCacheId })
		);
	}

	scanReceivingPackage(deliveryCacheId: number, packageLabel: string): Observable<MetrcReceivingState> {
		return this.http.post<MetrcReceivingState>(this.endpoint('metrc/receiving/scan'), {
			delivery_cache_id: deliveryCacheId,
			package_label: packageLabel,
		});
	}

	unscanReceivingPackage(deliveryCacheId: number, packageLabel: string): Observable<MetrcReceivingState> {
		return this.http.post<MetrcReceivingState>(this.endpoint('metrc/receiving/unscan'), {
			delivery_cache_id: deliveryCacheId,
			package_label: packageLabel,
		});
	}

	completeReceiving(
		deliveryCacheId: number,
		body: { notes?: string; allowShortage?: boolean } = {}
	): Observable<MetrcStandardResponse & { matched?: number; missing?: number; extra?: number }> {
		return this.http.post<any>(this.endpoint('metrc/receiving/complete'), {
			delivery_cache_id: deliveryCacheId,
			notes: body.notes ?? '',
			allow_shortage: body.allowShortage ? 1 : 0,
		});
	}

	getIncomingDeliveries(filter: { license?: string; manifest?: string; open_only?: boolean; limit?: number; offset?: number } = {}):
		Observable<MetrcIncomingDeliveriesResponse> {
		const q: any = { ...filter };
		if (q.open_only) q.open_only = 1;
		return this.http.get<MetrcIncomingDeliveriesResponse>(this.endpoint('metrc/transfers/incomingDeliveries', q));
	}

	previewIncomingPo(deliveryCacheId: number, opts: { supplierno?: string } = {}): Observable<MetrcIncomingPoPreview> {
		return this.http.get<MetrcIncomingPoPreview>(
			this.endpoint('metrc/receiving/previewPo', { delivery_cache_id: deliveryCacheId, ...opts })
		);
	}

	createIncomingPo(
		deliveryCacheId: number,
		opts: { supplierno?: string; allowPartial?: boolean; loccode?: string } = {}
	): Observable<MetrcIncomingPoResult> {
		return this.http.post<MetrcIncomingPoResult>(this.endpoint('metrc/receiving/createPo'), {
			delivery_cache_id: deliveryCacheId,
			supplierno: opts.supplierno ?? '',
			allow_partial: opts.allowPartial ? 1 : 0,
			loccode: opts.loccode ?? '',
		});
	}

	assignTransport(orderno: number, driverId: string | null, vehicleId: number | null): Observable<MetrcPickingState> {
		return this.http.post<MetrcPickingState>(this.endpoint('metrc/picking/assignTransport'), {
			orderno,
			driver_id: driverId ?? '',
			vehicle_id: vehicleId,
		});
	}

	scanPickingTag(body: MetrcPickingScanBody): Observable<MetrcPickingState> {
		return this.http.post<MetrcPickingState>(this.endpoint('metrc/picking/scan'), body);
	}

	splitPickingTag(body: MetrcPickingSplitBody): Observable<MetrcPickingState> {
		return this.http.post<MetrcPickingState>(this.endpoint('metrc/picking/split'), body);
	}

	unassignPickingTag(orderno: number, id: number): Observable<MetrcPickingState> {
		return this.http.post<MetrcPickingState>(this.endpoint('metrc/picking/unassign'), { orderno, id });
	}

	completePicking(orderno: number): Observable<MetrcStandardResponse & { orderno: number; job_id?: number; state?: string; shortages?: MetrcPickingShortage[]; pending_splits?: Array<{ orderlineno: number; child_label: string }> }> {
		return this.http.post<any>(this.endpoint('metrc/picking/complete'), { orderno });
	}

	/**
	 * Correct the customer's METRC recipient license from the picker and clear the
	 * order's recipient-license rejection. Writes debtorsmaster.metrc_recipient_license
	 * (the full suffixed form METRC requires) and returns the refreshed picking state.
	 */
	setPickingRecipientLicense(orderno: number, licenseNumber: string): Observable<MetrcPickingState> {
		return this.http.post<MetrcPickingState>(this.endpoint('metrc/picking/setRecipientLicense'), {
			orderno,
			license_number: licenseNumber,
		});
	}

	lookupPickingPackage(label: string, license?: string): Observable<MetrcStandardResponse & { package?: MetrcPackage }> {
		return this.http.post<any>(this.endpoint('metrc/picking/lookup'), { label, license });
	}

	setLicenseRequirePicking(license: string, require: boolean): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(
			this.endpoint('metrc/picking/setRequirePicking'),
			{ license, require: require ? 1 : 0 }
		);
	}

	// ── Pre-split (case break) inventory ────────────────────────────────────
	getPresplitQueue(opts: { q?: string; only_multi?: boolean; limit?: number } = {}): Observable<MetrcPresplitQueueResponse> {
		const q: any = { ...opts };
		if (q.only_multi) q.only_multi = 1;
		return this.http.get<MetrcPresplitQueueResponse>(this.endpoint('metrc/presplit/queue', q));
	}

	getPresplitState(parentLabel: string): Observable<MetrcPresplitState> {
		return this.http.get<MetrcPresplitState>(this.endpoint('metrc/presplit', { parent_label: parentLabel }));
	}

	splitPresplit(body: MetrcPresplitSplitBody): Observable<MetrcPresplitState> {
		return this.http.post<MetrcPresplitState>(this.endpoint('metrc/presplit/split'), body);
	}
}

export interface MetrcPickedPackageRow {
	id: number;
	orderno: number;
	orderlineno: number;
	metrc_package_label: string;
	package_id: number | null;
	quantity: number | string;
	picked_by: string | null;
	picked_at: string | null;
	is_substitution: 0 | 1 | boolean;
	substitution_reason: string | null;
	/** Picker scanned past the line's ordered qty and explicitly confirmed the over. */
	is_overpick: 0 | 1 | boolean;
	overpick_reason: string | null;
	pending_split: 0 | 1 | boolean;
	/** Populated when a METRC split (repack) was rejected; row kept at quantity 0 so the picker sees why. */
	split_error?: string | null;
	location_name?: string;
}

export interface MetrcPickingLine {
	orderlineno: number;
	stkcode: string;
	metrc_item_id: number;
	match_item_name: string;
	/** match_item_name when set, else stockmaster.description — UI display fallback */
	description?: string;
	quantity_ordered: number | string;
	quantity_picked: number | string;
	quantity_remaining: number | string;
	/** Confirmed over-pick on this line — quantity_remaining is clamped at 0 and hides it. */
	quantity_over?: number | string;
	/** Units per case (stockmaster.eoq); 0/absent when the item is not cased. */
	case_qty?: number | string;
	unit_price?: number | string;
	discount_percent?: number | string;
	extended_price?: number | string;
	picked_packages: MetrcPickedPackageRow[];
}

export interface MetrcPickingOrderHeader {
	debtorno: string;
	customer_name: string | null;
	branchcode: string;
	branch_name: string | null;
	ship_to_address?: string;
	orddate: string | null;
	deliverydate: string | null;
	comments: string | null;
	contactphone: string | null;
	contactemail: string | null;
	deliverto: string | null;
}

export interface MetrcPickingTotals {
	line_count: number;
	quantity_ordered: number | string;
	quantity_picked: number | string;
	order_total: number | string;
}

export interface MetrcTransporterDefaults {
	phone?: string | null;
	driver_id?: string | null;
	driver_name?: string | null;
	driver_employee_id?: string | null;
	driver_license?: string | null;
	vehicle_id?: number | null;
	vehicle_make?: string | null;
	vehicle_model?: string | null;
	vehicle_plate?: string | null;
}

export interface MetrcAvailableDriver {
	driver_id: string;
	name: string;
	employee_id: string | null;
	license_number: string | null;
	metrc_id: number | null;
}

export interface MetrcAvailableVehicle {
	id: number;
	plate: string;
	make: string | null;
	model: string | null;
	metrc_id: number | null;
}

export interface MetrcPickingRecipient {
	debtorno: string;
	name: string;
	/** Resolved license METRC validates — metrc_recipient_license preferred, else cosm_license. */
	license_number: string;
	license_source: 'metrc_recipient_license' | 'cosm_license' | '';
	cosm_license: string;
	lic_exp: string;
	expired: boolean | null;
	missing: boolean;
}

export interface MetrcPickingState {
	success: boolean;
	orderno: number;
	license_number: string;
	state: string | null;
	requires_picking: boolean;
	all_lines_covered: boolean;
	has_pending_splits: boolean;
	has_split_errors?: boolean;
	last_error?: string | null;
	recipient?: MetrcPickingRecipient | null;
	order_header?: MetrcPickingOrderHeader | null;
	totals?: MetrcPickingTotals;
	lines: MetrcPickingLine[];
	transporter_defaults?: MetrcTransporterDefaults;
	available_drivers?: MetrcAvailableDriver[];
	available_vehicles?: MetrcAvailableVehicle[];
	metrc_transfer_id?: string | null;
	metrc_manifest_id?: string | null;
	error?: string;
	message?: string;
}

export interface MetrcPickingScanBody {
	orderno: number;
	package_label: string;
	quantity?: number;
	orderlineno?: number;
	allow_substitution?: boolean;
	substitution_reason?: string;
	/** Confirm a scan that takes the line past its ordered qty (server 409s without it). */
	allow_overpick?: boolean;
	overpick_reason?: string;
}

export interface MetrcPickingSplitBody {
	orderno: number;
	orderlineno: number;
	parent_label: string;
	child_label: string;
	quantity: number;
	allow_substitution?: boolean;
	substitution_reason?: string;
	allow_overpick?: boolean;
	overpick_reason?: string;
}

// ── Pre-split (case break) inventory ──────────────────────────────────────
// No lineage table: a child unit lives in metrc_package_snapshot + the ERP lot
// store (stockserialitems / lot). Endpoints just read the parent case from the
// snapshot; the worker screen tracks the current session's children client-side.
export interface MetrcPresplitParentRow {
	package_label: string;
	package_id: number;
	item_id: number;
	item_name: string;
	quantity: number | string;
	unit_of_measure: string;
	location_name: string | null;
}

export interface MetrcPresplitQueueResponse {
	success: boolean;
	license_number: string;
	parents: MetrcPresplitParentRow[];
	error?: string;
	message?: string;
}

export interface MetrcPresplitState {
	success: boolean;
	license_number: string;
	parent: {
		package_label: string;
		package_id: number;
		item_id: number;
		item_name: string;
		quantity: number;
		unit_of_measure: string;
		location_name: string | null;
	};
	error?: string;
	message?: string;
}

export interface MetrcPresplitSplitBody {
	parent_label: string;
	child_label: string;
	quantity: number;
	location_name: string;
	item_name?: string;
}

export interface MetrcPickingShortage {
	orderlineno: number;
	stkcode: string;
	remaining: number | string;
}

export interface MetrcExpectedPackage {
	package_label: string;
	package_id: number;
	product_name: string;
	shipped_qty: number | string;
	uom: string;
	scanned_by?: string | null;
	scanned_at?: string | null;
}

export interface MetrcReceivingHeader {
	delivery_cache_id: number;
	transfer_id: string | null;
	delivery_id: string | null;
	manifest_number: string | null;
	shipper_facility_name: string | null;
	estimated_arrival_datetime: string | null;
	received_datetime: string | null;
	purchorder_orderno?: number | null;
	purch_import_state?: string | null;
	purch_supplierno?: string | null;
}

export interface MetrcReceivingSession {
	id: number;
	state: string;
	notes: string;
}

export interface MetrcReceivingScanRow {
	package_label: string;
	scanned_by?: string | null;
	scanned_at?: string | null;
}

export interface MetrcReceivingState {
	success: boolean;
	header: MetrcReceivingHeader;
	expected: MetrcExpectedPackage[];
	scanned: MetrcReceivingScanRow[];
	matched: MetrcExpectedPackage[];
	missing: MetrcExpectedPackage[];
	extra: MetrcReceivingScanRow[];
	counts: {
		expected: number;
		scanned: number;
		matched: number;
		missing: number;
		extra: number;
	};
	session: MetrcReceivingSession | null;
}

export interface MetrcIncomingDeliveryPackage {
	package_label: string;
	product_name: string;
	shipped_qty: number;
	uom: string;
	stkcode: string | null;
	map_status: string;
	map_reason: string | null;
}

export interface MetrcIncomingDeliveryRow {
	delivery_cache_id: number;
	transfer_id: string | null;
	delivery_id: string | null;
	manifest_number: string | null;
	shipper_facility_name: string | null;
	shipper_facility_license: string | null;
	package_count: number;
	received_datetime: string | null;
	estimated_arrival_datetime: string | null;
	receive_session_state: string | null;
	receive_session_id: number | null;
	purchorder_orderno: number | null;
	purch_import_state: string | null;
	purch_supplierno: string | null;
	packages: MetrcIncomingDeliveryPackage[];
	po_line_count: number;
	po_postable_lines: number;
	po_unmapped_lines: number;
	total_qty: number;
}

export interface MetrcIncomingDeliveriesResponse {
	success: boolean;
	ok?: boolean;
	rows: MetrcIncomingDeliveryRow[];
	total: number;
}

export interface MetrcIncomingPoPreview {
	success: boolean;
	ok?: boolean;
	delivery: Partial<MetrcIncomingDeliveryRow>;
	aggregated_lines: Array<{
		stkcode: string;
		product_name: string;
		quantity: number;
		uom: string;
		postable: boolean;
		map_status: string;
	}>;
	supplier: { ok: boolean; supplierno?: string; suppname?: string; match?: string; error?: string; candidates?: Array<{ supplierid: string; suppname: string }> };
	existing_import: { purchorder_orderno?: number; state?: string } | null;
	already_received_in_metrc: boolean;
}

export interface MetrcIncomingPoResult {
	success: boolean;
	ok?: boolean;
	state: 'posted' | 'already_posted';
	purchorder_orderno: number;
	import_id?: number;
	already_received_in_metrc?: boolean;
	line_count?: number;
	skipped?: unknown[];
	error?: string;
	supplier_candidates?: Array<{ supplierid: string; suppname: string }>;
}
